import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner } from '../components/ui';
import BodyMapCanvas from '../components/body-map/BodyMapCanvas';
import BodyIssueList from '../components/body-map/BodyIssueList';
import { getBodyRegionDefinition } from '../data/bodyMapRegions';
import useBodyMapIssues from '../hooks/useBodyMapIssues';
import { useAuth } from '../context/AuthContext';
import { PageHeader, StatRow } from '../components/layout/memberPrimitives';
import { createBodyIssue, listPendingBodyIssueFollowups } from '../services/bodyMapIssueService';
import type { BodyIssueFollowupRecord } from '../types/bodyIssueFollowup';

export default function BodyMapPage() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { loading, issues, selectedRegionKey, setSelectedRegionKey, refresh } = useBodyMapIssues(currentUser?.uid);
  const [creating, setCreating] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [followups, setFollowups] = useState<BodyIssueFollowupRecord[]>([]);
  const [form, setForm] = useState<{
    title: string;
    symptomSummary: string;
    knownContext: string;
    onsetType: 'sudden' | 'gradual' | 'unknown';
    severityBaseline: string;
  }>({
    title: '',
    symptomSummary: '',
    knownContext: '',
    onsetType: 'unknown' as const,
    severityBaseline: '',
  });

  const selectedRegion = useMemo(
    () => (selectedRegionKey ? getBodyRegionDefinition(selectedRegionKey) : null),
    [selectedRegionKey],
  );
  const activeIssues = useMemo(
    () => issues.filter((issue) => issue.status !== 'resolved'),
    [issues],
  );
  const exploredRegions = useMemo(
    () => new Set(issues.map((issue) => issue.regionKey)).size,
    [issues],
  );
  const affectedSystems = useMemo(
    () => Array.from(new Set(issues.flatMap((issue) => issue.systemTags))),
    [issues],
  );
  const dueFollowups = useMemo(
    () => followups.filter((item) => {
      const dueDate = (item.dueAt as { toDate?: () => Date })?.toDate?.() ?? new Date(item.dueAt as string);
      return !Number.isNaN(dueDate.getTime()) && dueDate.getTime() <= Date.now();
    }),
    [followups],
  );

  const refreshEverything = async () => {
    await refresh();
    if (currentUser?.uid) {
      setFollowups(await listPendingBodyIssueFollowups(currentUser.uid));
    }
  };

  const handleCreateIssue = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!currentUser || !selectedRegion || !form.title.trim()) return;
    setCreating(true);
    setNotice(null);
    try {
      const issueId = await createBodyIssue(currentUser.uid, {
        title: form.title,
        regionKey: selectedRegion.key,
        side: selectedRegion.side,
        systemTags: selectedRegion.systemTags,
        status: 'active',
        onsetType: form.onsetType,
        severityBaseline: form.severityBaseline ? Number(form.severityBaseline) : null,
        symptomSummary: form.symptomSummary,
        knownContext: form.knownContext,
        startedAtLabel: null,
      });
      await refreshEverything();
      setForm({
        title: '',
        symptomSummary: '',
        knownContext: '',
        onsetType: 'unknown',
        severityBaseline: '',
      });
      setNotice('Issue created.');
      navigate(`/body-map/issues/${issueId}`);
    } finally {
      setCreating(false);
    }
  };

  React.useEffect(() => {
    const loadFollowups = async () => {
      if (!currentUser?.uid) return;
      setFollowups(await listPendingBodyIssueFollowups(currentUser.uid));
    };
    loadFollowups();
  }, [currentUser]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-8 melio-stagger">
      <PageHeader
        breadcrumb={[{ label: 'Home', href: '/home' }, { label: 'Body map' }]}
        eyebrow="Structured body map"
        title="Track issues by body region"
        subtitle="Start with the area affected, then build a structured timeline of symptoms, prior evidence, and follow-up — not scattered notes."
      />

      {notice && (
        <section className="rounded-2xl border px-5 py-4 mb-6 bg-white" style={{ borderColor: 'var(--melio-border)', color: 'var(--melio-ink)' }}>
          {notice}
        </section>
      )}

      <StatRow
        items={[
          { label: 'Active issues', value: activeIssues.length },
          { label: 'Explored regions', value: exploredRegions },
          {
            label: 'Affected systems',
            value: affectedSystems.length ? String(affectedSystems.length) : 'None noted',
            hint: affectedSystems.length ? affectedSystems.slice(0, 4).join(' · ') : 'No systems mapped yet',
          },
        ]}
      />

      <section className="mt-6">
        <BodyMapCanvas selectedRegionKey={selectedRegionKey} onSelectRegion={setSelectedRegionKey} issues={issues} />
      </section>

      <section className="mt-6">
        <BodyIssueList issues={issues} selectedRegionKey={selectedRegionKey} />
      </section>

      {dueFollowups.length > 0 && (
        <section className="rounded-3xl border p-6 md:p-8 bg-white mt-6" style={{ borderColor: 'var(--melio-border)' }}>
          <p className="text-xs uppercase tracking-[0.2em] mb-3" style={{ color: 'var(--melio-sage)' }}>
            Follow-up due
          </p>
          <h2 className="text-2xl font-normal mb-5" style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-charcoal)' }}>
            Melio is waiting for an update
          </h2>
          <div className="space-y-3">
            {dueFollowups.map((followup) => (
              <button
                key={followup.id}
                type="button"
                onClick={() => navigate(`/body-map/issues/${followup.issueId}`)}
                className="w-full text-left rounded-2xl border px-4 py-4 transition-opacity hover:opacity-90"
                style={{ borderColor: 'var(--melio-border)', backgroundColor: 'var(--b-bone)' }}
              >
                <div className="font-medium mb-1" style={{ color: 'var(--melio-charcoal)' }}>{followup.title}</div>
                <div className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>{followup.message}</div>
              </button>
            ))}
          </div>
        </section>
      )}

      <section className="rounded-3xl border p-6 md:p-8 bg-white mt-6" style={{ borderColor: 'var(--melio-border)' }}>
        <p className="text-xs uppercase tracking-[0.2em] mb-3" style={{ color: 'var(--melio-sage)' }}>
          Create issue
        </p>
        <h2 className="text-2xl font-normal mb-5" style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-charcoal)' }}>
          {selectedRegion ? `Start tracking ${selectedRegion.label.toLowerCase()}` : 'Choose a region first'}
        </h2>
        {!selectedRegion ? (
          <p className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>
            Select a region above to create a structured issue record.
          </p>
        ) : (
          <form onSubmit={handleCreateIssue} className="space-y-4">
            <label className="block">
              <span className="block text-sm font-medium mb-1.5" style={{ color: 'var(--melio-charcoal)' }}>Issue title</span>
              <input
                value={form.title}
                onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
                className="w-full rounded-xl border px-4 py-3 text-sm"
                style={{ borderColor: 'var(--melio-border)', backgroundColor: 'var(--b-bone)', color: 'var(--melio-charcoal)' }}
                placeholder="e.g. Recurring sesamoid flare"
              />
            </label>
            <div className="grid md:grid-cols-2 gap-4">
              <label className="block">
                <span className="block text-sm font-medium mb-1.5" style={{ color: 'var(--melio-charcoal)' }}>Onset</span>
                <select
                  value={form.onsetType}
                  onChange={(event) => setForm((current) => ({ ...current, onsetType: event.target.value as 'sudden' | 'gradual' | 'unknown' }))}
                  className="w-full rounded-xl border px-4 py-3 text-sm"
                  style={{ borderColor: 'var(--melio-border)', backgroundColor: 'var(--b-bone)', color: 'var(--melio-charcoal)' }}
                >
                  <option value="unknown">Unknown</option>
                  <option value="sudden">Sudden</option>
                  <option value="gradual">Gradual</option>
                </select>
              </label>
              <label className="block">
                <span className="block text-sm font-medium mb-1.5" style={{ color: 'var(--melio-charcoal)' }}>Baseline severity</span>
                <input
                  type="number"
                  min={0}
                  max={10}
                  value={form.severityBaseline}
                  onChange={(event) => setForm((current) => ({ ...current, severityBaseline: event.target.value }))}
                  className="w-full rounded-xl border px-4 py-3 text-sm"
                  style={{ borderColor: 'var(--melio-border)', backgroundColor: 'var(--b-bone)', color: 'var(--melio-charcoal)' }}
                />
              </label>
            </div>
            <label className="block">
              <span className="block text-sm font-medium mb-1.5" style={{ color: 'var(--melio-charcoal)' }}>Symptom summary</span>
              <textarea
                rows={3}
                value={form.symptomSummary}
                onChange={(event) => setForm((current) => ({ ...current, symptomSummary: event.target.value }))}
                className="w-full rounded-xl border px-4 py-3 text-sm"
                style={{ borderColor: 'var(--melio-border)', backgroundColor: 'var(--b-bone)', color: 'var(--melio-charcoal)' }}
                placeholder="What is the issue and how does it usually feel?"
              />
            </label>
            <label className="block">
              <span className="block text-sm font-medium mb-1.5" style={{ color: 'var(--melio-charcoal)' }}>Known context</span>
              <textarea
                rows={3}
                value={form.knownContext}
                onChange={(event) => setForm((current) => ({ ...current, knownContext: event.target.value }))}
                className="w-full rounded-xl border px-4 py-3 text-sm"
                style={{ borderColor: 'var(--melio-border)', backgroundColor: 'var(--b-bone)', color: 'var(--melio-charcoal)' }}
                placeholder="Prior imaging, treatment, orthotics, injections, rehab, or anything else relevant"
              />
            </label>
            <button
              type="submit"
              disabled={creating || !form.title.trim()}
              className="inline-flex items-center justify-center px-5 py-3 rounded text-sm font-medium transition-opacity hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: 'var(--melio-charcoal)', color: 'var(--melio-cream)' }}
            >
              {creating ? 'Creating...' : 'Create issue'}
            </button>
          </form>
        )}
      </section>
    </div>
  );
}
